import React from "react";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import * as t from "@/lib/backend/manufacturing2.generated.types";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import Loader from "@/modules/shared/ui/Loader";
type Props = {
    competences: t.ILineCompetenceData[];
    loading: boolean;
    error?: string;
    active_warning?: string;
    only_active: boolean;
    person_active_by_uuid: Map<string, boolean>;
    person_active_enrichment_failed: boolean;
    total_competence_count: number;
    active_competence_count: number;
};

function getCompetenceLabel(level: number): string {
    if (level === 1) {
        return translate("Manufacturing.People.competence_level.1", "Learning");
    }
    if (level === 2) {
        return translate("Manufacturing.People.competence_level.2", "Capable");
    }
    if (level === 3) {
        return translate("Manufacturing.People.competence_level.3", "Independent");
    }
    return translate("Manufacturing.People.competence_level.0", "None");
}

/**
 * Read-only list of people with line-level competence on a manufacturing line.
 */
const LinesCompetentPeople = ({
    competences,
    loading,
    error,
    active_warning,
    only_active,
    person_active_by_uuid,
    person_active_enrichment_failed,
    total_competence_count,
    active_competence_count,
}: Props) => {
    if (loading) {
        return (
            <div className="list-group list-group-flush">
                <div className="list-group-item">
                    <Loader small />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="list-group list-group-flush">
                <div className="list-group-item">
                    <ErrorComponent msg={error} type="error" />
                </div>
            </div>
        );
    }

    return (
        <>
            {active_warning ? <ErrorComponent msg={active_warning} type="warning" /> : null}
            {renderBody()}
        </>
    );

    function renderBody() {
        if (total_competence_count === 0) {
            return (
                <div className="list-group list-group-flush">
                    <div className="list-group-item">
                        <FormattedMessage
                            id="Manufacturing.Lines.no_competent_people"
                            defaultMessage="No competent people"
                        />
                    </div>
                </div>
            );
        }

        if (competences.length === 0 && only_active && active_competence_count === 0) {
            return (
                <div className="list-group list-group-flush">
                    <div className="list-group-item">
                        <FormattedMessage
                            id="Manufacturing.Lines.no_competent_active_but_inactive_exist"
                            defaultMessage="No competent active people, but inactive competent people exist."
                        />
                    </div>
                </div>
            );
        }

        return (
            <div className="list-group list-group-flush lines-competent-people">
                {competences.map((competence, i) => {
                    const person_inactive =
                        !person_active_enrichment_failed && person_active_by_uuid.get(competence.person_uuid) === false;
                    return (
                        <Link
                            key={`${competence.person_uuid}_${competence.line_uuid}_${competence.process_uuid ?? ""}_${competence.level}_${i}`}
                            className="list-group-item list-group-item-action d-flex align-items-center"
                            to={`/digital-twin/resources/people/${competence.person_uuid}`}
                            title={translate("Manufacturing.Lines.view_person", "View person")}
                            data-testid="competence-row"
                        >
                            <span className="lines-competent-people__name">
                                {competence.person_name}
                                {person_inactive ? (
                                    <span className="badge-list ml-1">
                                        <span className="badge badge-secondary">
                                            <FormattedMessage id="common.inactive" defaultMessage="inactive" />
                                        </span>
                                    </span>
                                ) : null}
                            </span>
                            <span
                                className={`badge badge-competence badge-competence-${competence.level}`}
                                data-testid="competence-level"
                            >
                                {getCompetenceLabel(competence.level)}
                            </span>
                        </Link>
                    );
                })}
            </div>
        );
    }
};

export default LinesCompetentPeople;
